import { useState, useEffect } from "react";
import { MenuBackdrop } from "@/components/MenuBackdrop";
import RotatingBackground from "@/components/RotatingBackground";
import TitleMusic from "@/components/TitleMusic";
import { FitScreen } from "@/components/FitScreen";
import TitleSettings from "@/components/screens/TitleSettings";
import SponsorModal from "@/components/SponsorModal";
import InviteModal from "@/components/InviteModal";
import WeeklyWinners from "@/components/WeeklyWinners";
import FreeGiftsModal from "@/components/FreeGiftsModal";
import { ReviewsCorners } from "@/components/ReviewsCorners";
import WeeklyPrizeBanner from "@/components/WeeklyPrizeBanner";
import { UnlockProgress } from "@/components/UnlockProgress";
import InstallPrompt from "@/components/InstallPrompt";
import SoundtrackModal from "@/components/SoundtrackModal";
import { LangToggle } from "@/components/LangToggle";
import { LEVELS_META, CHARACTERS, hasNearbyUnlock } from "@/game/config";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import { CalendarDays, LogIn, LogOut, Lightbulb, BarChart3, Settings, Handshake, UserPlus, Trophy, Coins, Sparkles, Flame, Fish, Gift } from "lucide-react";

const SpotifyGlyph = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.5 17.32c-.22.36-.68.47-1.04.25-2.86-1.75-6.46-2.14-10.7-1.18-.41.1-.82-.16-.92-.57-.1-.41.16-.82.57-.92 4.64-1.06 8.62-.6 11.83 1.36.36.22.47.69.26 1.06zm1.47-3.27c-.27.45-.86.6-1.31.32-3.27-2.01-8.26-2.6-12.13-1.42-.51.15-1.05-.13-1.21-.64-.15-.51.14-1.05.65-1.2 4.42-1.34 9.92-.69 13.68 1.62.45.28.6.87.32 1.32zm.13-3.4C15.18 8.26 8.9 8.04 5.27 9.14c-.61.18-1.26-.16-1.44-.77-.18-.61.16-1.26.77-1.44 4.17-1.27 11.1-1.02 15.48 1.58.55.32.73 1.03.4 1.58-.32.54-1.03.72-1.58.4z" />
  </svg>
);

export default function TitleScreen({ onStart, onDaily, onLeaderboard, onAuth, onSuggest, onAdmin, onShowNewChars, onTournament, onFishing, onCollection, daily }) {
  const { t, lang } = useLang();
  const { user, isAdmin, logout } = useAuth();
  const [showSettings, setShowSettings] = useState(false);
  const [waitlist, setWaitlist] = useState(false); // Coming Soon "notify me" modal (public)
  const [wlEmail, setWlEmail] = useState("");
  const [wlDone, setWlDone] = useState(false);
  const [wlBusy, setWlBusy] = useState(false);
  const submitWaitlist = async (e) => {
    e && e.preventDefault();
    if (!wlEmail || !/.+@.+\..+/.test(wlEmail) || wlBusy) return;
    setWlBusy(true);
    try { await api.fishingWaitlist(wlEmail.trim()); setWlDone(true); } catch (err) { /* noop */ } finally { setWlBusy(false); }
  };
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== "undefined" && (window.innerWidth < 900 || window.innerHeight < 520));
  useEffect(() => {
    const f = () => setIsNarrow(window.innerWidth < 900 || window.innerHeight < 520);
    window.addEventListener("resize", f);
    window.addEventListener("orientationchange", f);
    return () => { window.removeEventListener("resize", f); window.removeEventListener("orientationchange", f); };
  }, []);
  const [showInstall, setShowInstall] = useState(false);
  const [showSponsor, setShowSponsor] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showWinners, setShowWinners] = useState(false);
  const [showFreeGifts, setShowFreeGifts] = useState(false);
  const [discountCount, setDiscountCount] = useState(0);
  useEffect(() => {
    api.homeBillboards()
      .then((r) => setDiscountCount((r.data || []).filter((b) => (b.discount_code || "").trim()).length))
      .catch(() => {});
  }, []);
  const [showSoundtrack, setShowSoundtrack] = useState(false);
  const assetBase = `${process.env.PUBLIC_URL || ""}/game-assets`;
  const newChars = CHARACTERS.filter((c) => c.isNew).slice(0, 4);
  const dailyLevel = daily ? LEVELS_META[daily.levelIndex] : null;
  const diffLabel = daily ? t(`diff${daily.diffId.charAt(0).toUpperCase() + daily.diffId.slice(1)}`) : "";

  // NOTE: Auto sign-up prompt and Free-Gifts auto-popup were removed to keep the
  // landing clean. Only Save-to-phone (then Register once) auto-open now — the
  // Free Gifts explainer still opens from its home-screen button.

  return (
    <div className="relative w-full h-full flex flex-col items-center overflow-y-auto scanlines">
      <div className="absolute inset-0 bg-[#0a0f1e]" />
      <RotatingBackground />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 90% 60% at 50% 82%, rgba(0,0,0,0.6), rgba(0,0,0,0.1))" }} />

      {/* Floating "New Characters" preview — top strip, left of the centered logo (out of the menu column and clear of the logo) */}
      {newChars.length > 0 && onShowNewChars && (
        <button
          data-testid="new-chars-preview-btn"
          onClick={onShowNewChars}
          style={{ top: "max(0.5rem, env(safe-area-inset-top))", left: "max(0.75rem, calc(env(safe-area-inset-left) + 0.5rem))" }}
          className="absolute z-[70] flex items-center gap-1.5 bg-black/65 backdrop-blur-md rounded-full pl-1.5 pr-2.5 py-1.5 ring-2 ring-[#FF1744]/70 hover:scale-105 active:scale-95 transition-transform max-w-[46vw]"
        >
          <span className="flex -space-x-3 shrink-0">
            {newChars.map((c) => (
              <img
                key={c.id}
                src={`${assetBase}/${c.id}.webp`}
                alt={c.name}
                draggable={false}
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover object-top ring-2 ring-white/80 bg-gradient-to-b from-[#333] to-[#111]"
              />
            ))}
          </span>
          <span className="font-pixel text-[8px] sm:text-[11px] text-[#FFEB3B] flex items-center gap-1 animate-pulse whitespace-nowrap truncate">
            <Sparkles size={12} strokeWidth={3} className="shrink-0" /> {t("newCharsCta")}
          </span>
        </button>
      )}

      {/* Account bar */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
        <LangToggle />
        <button data-testid="title-settings-btn" onClick={() => setShowSettings(true)} className="pixel-btn bg-black/55 text-white p-2.5" aria-label="Settings">
          <Settings size={16} strokeWidth={2.5} />
        </button>
        {user ? (
          <>
            <span className="font-pixel text-[9px] text-white bg-black/55 px-3 py-2 rounded-lg" data-testid="account-username">{user.username}</span>
            {isAdmin && (
              <button data-testid="admin-btn" onClick={onAdmin} className="pixel-btn bg-[#9c27b0] text-white p-2.5"><BarChart3 size={16} strokeWidth={2.5} /></button>
            )}
            <button data-testid="logout-btn" onClick={logout} className="pixel-btn bg-black/55 text-white p-2.5"><LogOut size={16} strokeWidth={2.5} /></button>
          </>
        ) : (
          <button data-testid="signin-btn" onClick={onAuth} className="pixel-btn bg-black/55 text-white px-3 py-2 text-[10px] flex items-center gap-2"><LogIn size={14} strokeWidth={2.5} /> {t("signIn")}</button>
        )}
      </div>

      <div className="relative w-full flex-1 min-h-0 z-10 text-center">
       <FitScreen maxScale={2.9} fill={1} scroll={false}>
       <div className="flex flex-col items-center px-6 pt-2 sm:pt-4 pb-10">
        <h1 className="sr-only">Nica Bros — Juego de Nicaragua. Jugá gratis el mejor juego nica en línea: corré por Managua, Granada y San Juan del Sur, un plataformas pinolero. · The #1 free Nicaragua arcade game.</h1>
        <div className="h-[2vh] sm:h-[3vh] shrink-0" aria-hidden="true" />

        <img
          src={`${process.env.PUBLIC_URL || ""}/icon-192.png`}
          alt="Nica Bros"
          data-testid="title-logo"
          className="h-[16vh] sm:h-[21vh] w-auto object-contain mb-5 sm:mb-6 mx-auto rounded-2xl drop-shadow-[0_6px_18px_rgba(0,0,0,0.65)]"
        />

        <div className="w-full max-w-7xl flex items-start justify-center gap-3 sm:gap-4 mt-0">
          {/* Left: Weekend Tournament (opt-in) */}
          <div className="flex-[2.2] min-w-0 max-w-[430px] flex flex-col gap-3">
            <button data-testid="weekend-tournament-box-btn" onClick={onTournament} className="pixel-btn bg-[#0067C6] text-white w-full aspect-square p-3 text-2xl sm:text-3xl flex flex-col items-center justify-center gap-3 leading-tight text-center relative overflow-hidden">
              <div className="flex items-center gap-3"><Trophy size={44} strokeWidth={2.5} /><Fish size={44} strokeWidth={2.5} /></div>
              {t("weekendTournament")}
            </button>
            <button
              data-testid="register-now-badge"
              onClick={(e) => { e.stopPropagation(); onAuth("register"); }}
              className="register-pill pixel-btn w-full px-3 py-4 rounded-2xl bg-[#4CAF50] text-white ring-2 ring-white flex items-center justify-center gap-2 shadow-[0_6px_18px_rgba(0,0,0,0.5)]"
            >
              <UserPlus size={22} strokeWidth={3} className="text-white shrink-0" />
              <span className="font-extrabold text-[15px] sm:text-[17px] tracking-tight leading-tight text-center">{t("registerNow")}</span>
            </button>
          </div>

          {/* Center: main menu */}
          <div className="ts-menu-card flex-[3.2] max-w-5xl flex flex-col items-center gap-3 sm:gap-4 bg-black/60 backdrop-blur-md rounded-3xl px-7 py-6 sm:py-8 ring-1 ring-white/10">
          <button data-testid="start-game-btn" onClick={onStart} className="pixel-btn bg-[#FF5722] text-white w-full px-16 py-11 text-4xl md:text-5xl blink" style={{ boxShadow: "0 0 0 4px rgba(0,0,0,0.45), 0 14px 30px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.3)" }}>
            {t("start")}
          </button>

          <button data-testid="daily-challenge-btn" onClick={onDaily} className="pixel-btn bg-[#FFB300] text-black w-full px-10 py-7 text-2xl sm:text-3xl flex items-center justify-center gap-3">
            <CalendarDays size={32} strokeWidth={2.5} /> {t("dailyChallenge")}
          </button>
          {dailyLevel && (
            <p className="font-vt text-white/80 text-lg -mt-1 short-hide" data-testid="daily-teaser">
              {t("today")}: {dailyLevel.name.split(" ")[0]} · <span className="text-[#FFEB3B]">{diffLabel}</span>
            </p>
          )}

          <button data-testid="title-collection-btn" onClick={onCollection} className="relative pixel-btn bg-[#FFD400] text-black w-full px-10 py-8 text-2xl sm:text-3xl flex items-center justify-center gap-3">
            <img src={`${assetBase}/nicabros-logo.webp`} alt="" className="h-9 sm:h-10 w-auto object-contain" />
            {t("collectionBtn")}
          </button>

          <button
            data-testid="fishing-box-btn"
            onClick={user ? onFishing : onAuth}
            className={`relative pixel-btn text-white w-full px-10 py-8 text-2xl sm:text-3xl flex items-center justify-center gap-3 ${user ? "bg-[#0e7a63] blink" : "bg-[#146a58]"}`}
          >
            <Fish size={32} strokeWidth={2.5} /> {t("fishingTitle")}
            {user ? (
              <span className="absolute -top-3 -right-3 w-12 h-12 rounded-full bg-[#FFD400] text-black font-pixel text-[10px] flex items-center justify-center ring-2 ring-white shadow-[0_2px_8px_rgba(0,0,0,0.6)] rotate-6" data-testid="fishing-new-badge">{t("newBadge")}</span>
            ) : (
              <span className="absolute -top-3 -right-3 px-3 py-1.5 rounded-full bg-[#FF6D00] text-white font-pixel text-[9px] flex items-center justify-center ring-2 ring-white shadow-[0_2px_8px_rgba(0,0,0,0.6)] rotate-3 whitespace-nowrap" data-testid="fishing-signin-badge">{lang === "es" ? "INICIÁ SESIÓN" : "SIGN IN"}</span>
            )}
          </button>

          {user ? (
            <div className="flex flex-col items-center gap-2 w-full">
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <div data-testid="title-coin-balance" className="px-6 py-2.5 rounded-2xl bg-black/50 ring-2 ring-[#FFB300]/60 font-pixel text-xl md:text-2xl text-[#FFB300] flex items-center gap-2.5">
                  <Coins size={28} strokeWidth={2.5} /> {user.coins || 0} {t("coins")}
                </div>
                {(user.streak || 0) > 0 && (
                  <div data-testid="title-daily-streak" title={t("streakBadgeTitle")} className="px-5 py-2.5 rounded-2xl bg-black/50 ring-2 ring-[#FF6D00]/70 font-pixel text-xl md:text-2xl text-[#FF6D00] flex items-center gap-2">
                    <Flame size={26} strokeWidth={2.5} /> {user.streak}
                  </div>
                )}
              </div>
              <p data-testid="title-coin-hint" className="font-vt text-lg md:text-xl text-white/80 leading-tight max-w-md short-hide">{t("earnCoinsHint")}</p>
              <div className="short-hide w-full flex justify-center"><UnlockProgress /></div>
            </div>
          ) : (
            <p data-testid="signin-credit-note" className="font-vt text-base text-white/75 max-w-sm leading-tight short-hide">
              {t("signInForCredit")}{" "}
              <button onClick={onAuth} data-testid="signin-credit-link" className="text-[#FFEB3B] underline font-bold text-lg">{t("signInCta")}</button>
            </p>
          )}
        </div>

          {/* Right: Brand Partnerships + Free Gifts */}
          <div className="flex-[2.2] min-w-0 max-w-[430px] flex flex-col gap-3">
            <button data-testid="become-sponsor-btn" onClick={() => setShowSponsor(true)} className="pixel-btn bg-[#FF1744] text-white w-full aspect-square p-3 text-2xl sm:text-3xl flex flex-col items-center justify-center gap-3 leading-tight text-center">
              <Handshake size={56} strokeWidth={2.5} /> {t("becomeSponsor")}
            </button>
            <button
              data-testid="free-gifts-badge"
              onClick={() => setShowFreeGifts(true)}
              className="free-gifts-btn pixel-btn bg-[#4CAF50] text-white w-full px-3 py-4 rounded-2xl ring-2 ring-white/70 flex items-center justify-center gap-2"
            >
              <Sparkles size={22} strokeWidth={3} className="text-white shrink-0" />
              <span className="font-extrabold text-[15px] sm:text-[17px] tracking-tight leading-tight text-center">{t("freeGiftsBadgePlay")}</span>
            </button>
            <button
              data-testid="discount-count-badge"
              onClick={() => (user ? setShowSponsor(true) : onAuth())}
              className="pixel-btn bg-[#FF6D00] text-white w-full px-3 py-4 rounded-2xl ring-2 ring-white/70 flex items-center justify-center gap-2 animate-pulse"
            >
              <Gift size={22} strokeWidth={2.6} className="text-white shrink-0" />
              <span className="font-extrabold text-[15px] sm:text-[17px] tracking-tight leading-tight text-center">{user ? (discountCount > 0 ? `${discountCount} ${t("discountsUnlocked")}` : t("discountsBtn")) : t("signInForDiscounts")}</span>
            </button>
          </div>
        </div>

        <div className="flex items-stretch justify-center gap-3 sm:gap-4 mt-8 w-full max-w-4xl mx-auto">
          <button data-testid="suggest-btn" onClick={onSuggest} className="pixel-btn bg-[#FF1744] text-white flex-1 min-w-0 px-3 py-4 text-base sm:text-xl flex items-center justify-center gap-2 leading-tight text-center">
            <Lightbulb size={24} strokeWidth={2.5} className="shrink-0" /> {t("suggestIdeas")}
          </button>
          <button data-testid="leaderboard-btn" onClick={onLeaderboard} className="pixel-btn bg-[#FF5722] text-white flex-1 min-w-0 px-3 py-4 text-base sm:text-xl flex items-center justify-center gap-2 leading-tight text-center">
            {t("scores")}
          </button>
          <button data-testid="invite-friends-btn" onClick={() => setShowInvite(true)} className="pixel-btn bg-[#0067C6] text-white flex-1 min-w-0 px-3 py-4 text-base sm:text-xl flex items-center justify-center gap-2 leading-tight text-center">
            <UserPlus size={24} strokeWidth={2.5} className="shrink-0" /> {t("inviteFriends")}
          </button>
        </div>
       </div>
       </FitScreen>
      </div>

      <ReviewsCorners />

      <div className="relative z-20 w-full shrink-0 mt-1 flex items-end gap-2 px-2">
        <div className="shrink-0 w-64 sm:w-80 flex items-center gap-2 justify-start" data-testid="music-cluster">
          <button
            data-testid="soundtrack-btn"
            onClick={() => { api.trackSpotifyClick({ song_id: "soundtrack", title: "Soundtrack list", artist: "", source: "soundtrack_button" }); setShowSoundtrack(true); }}
            aria-label={t("soundtrack", "Soundtrack")}
            title={t("soundtrack", "Soundtrack")}
            className="shrink-0 w-11 h-11 rounded-full bg-[#1DB954] text-black flex items-center justify-center shadow-lg ring-2 ring-black/30 hover:scale-110 active:scale-95 transition-transform"
          >
            <SpotifyGlyph size={24} />
          </button>
          <TitleMusic />
        </div>
        <div className="flex-1 flex justify-center min-w-0">
          <div className="w-full max-w-md px-1"><WeeklyPrizeBanner /></div>
        </div>
        <div className="shrink-0 w-64 sm:w-80 hidden sm:block" aria-hidden="true" />
      </div>

      <a
        href="https://www.GregMiceli.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="created-by-credit"
        className="relative z-20 shrink-0 mt-1 mb-1.5 font-vt text-xs text-white/55 underline underline-offset-2 hover:text-white/90 transition-colors whitespace-nowrap"
      >
        Created by www.GregMiceli.com
      </a>

      {showSettings && <TitleSettings onClose={() => setShowSettings(false)} onSaveToPhone={() => setShowInstall(true)} />}
      {showInstall && <InstallPrompt onClose={() => setShowInstall(false)} />}
      {showSponsor && <SponsorModal onClose={() => setShowSponsor(false)} />}
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onSignIn={() => { setShowInvite(false); onAuth(); }} />}
      {showWinners && <WeeklyWinners onClose={() => setShowWinners(false)} />}
      {showFreeGifts && (
        <FreeGiftsModal
          onClose={() => setShowFreeGifts(false)}
          onWeeklyWinners={() => { setShowFreeGifts(false); setShowWinners(true); }}
          onBrandPartnerships={() => { setShowFreeGifts(false); setShowSponsor(true); }}
        />
      )}
      {showSoundtrack && <SoundtrackModal onClose={() => setShowSoundtrack(false)} />}
      {waitlist && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/80 p-6 overflow-y-auto" data-testid="fishing-waitlist-modal" onClick={() => setWaitlist(false)}>
          <div className="pixel-card bg-[#0e2c3f] p-6 max-w-sm w-full text-center relative my-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setWaitlist(false)} data-testid="waitlist-close" className="absolute top-2 right-3 text-white/60 hover:text-white text-2xl leading-none">×</button>
            <div className="text-5xl mb-2">🎣</div>
            <h2 className="font-pixel text-sm text-[#FFEB3B] pixel-stroke mb-2">{lang === "es" ? "¡Muy Pronto!" : "Coming Soon!"}</h2>
            {wlDone ? (
              <div data-testid="waitlist-success">
                <div className="text-4xl mb-2">✅</div>
                <p className="font-vt text-lg text-white/90">{lang === "es" ? "¡Listo! Te avisaremos apenas se lance NicaBros Fishing." : "You're on the list! We'll email you the moment NicaBros Fishing launches."}</p>
                <button onClick={() => setWaitlist(false)} data-testid="waitlist-done" className="pixel-btn bg-[#2e7d32] text-white px-5 py-2.5 text-[10px] mt-4">{lang === "es" ? "¡Genial!" : "Sweet!"}</button>
              </div>
            ) : (
              <form onSubmit={submitWaitlist}>
                <p className="font-vt text-lg text-white/85 mb-4">{lang === "es" ? "NicaBros Fishing está en camino. Dejá tu correo y te avisamos cuando esté listo." : "NicaBros Fishing is almost here. Drop your email and we'll notify you when it launches."}</p>
                <input type="email" value={wlEmail} onChange={(e) => setWlEmail(e.target.value)} data-testid="waitlist-email" required placeholder={lang === "es" ? "tu@correo.com" : "you@email.com"}
                  className="w-full font-vt text-lg px-4 py-3 rounded-lg bg-black/50 text-white border-2 border-white/20 outline-none focus:border-[#FFD54F] text-center" />
                <button type="submit" disabled={wlBusy} data-testid="waitlist-submit" className="pixel-btn bg-[#FF6D00] text-white w-full px-5 py-3 text-[11px] mt-4 disabled:opacity-60">{wlBusy ? (lang === "es" ? "Enviando…" : "Sending…") : (lang === "es" ? "Avísame" : "Notify Me")}</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
